import { createRef, useEffect } from "react";
import {
  Box,
  Container,
  Typography as MuiTypography,
  List,
  ListItemIcon,
  Avatar,
} from "@material-ui/core";
import styled from "@emotion/styled";
import { useRouter } from "next/router";

import BackgroundWavesLeft from "@/public/images/WhatIsCyberInsuranceBgWavesLeft.svg";
import BackgroundWavesRight1 from "@/public/images/WhatIsCyberInsuranceBgWavesRight1.svg";
import BackgroundWavesRight2 from "@/public/images/WhatIsCyberInsuranceBgWavesRight2.svg";
import BackgroundWavesRight3 from "@/public/images/WhatIsCyberInsuranceBgWavesRight3.svg";
import ReadyToQuoteBgWavesLeft from "@/public/images/ReadyToQuoteBgWavesLeft.svg";
import ReadyToQuoteBgWavesRight from "@/public/images/ReadyToQuoteBgWavesRight.svg";
import Password from "@/public/icons/Password.svg";
import PaperFail from "@/public/icons/PaperFail.svg";
import ShieldFail from "@/public/icons/ShieldFail.svg";
import Calendar from "@/public/icons/Calendar.svg";
import DownArrow from "@/public/icons/DownArrow.svg";
import DangerIcon from "@/public/icons/Danger.svg";
import CostIcon from "@/public/icons/Cost.svg";
import ContractIcon from "@/public/icons/Contract.svg";
import MainLayout from "@/layouts/MainLayout";
import Links from "@/constants/Links";
import { ButtonLink } from "@/components/Buttons";
import { useIsLarge, useIsMedium, useIsMobile } from "@/styles/breakpoints";
import CoverageAccordion from "@/components/CoverageAccordion";

const StyledContainer = styled(Container)`
  padding: 32px;
  margin-top: 60px;
  max-width: 800px;
  min-height: 1000px;
`;

const Typography = styled(MuiTypography)`
  color: #21245c;
`;

const MainHeader = styled(Typography)`
  font-size: 72px;
  line-height: 84px;

  @media (max-width: 768px) {
    font-size: 56px;
    line-height: 70px;
  }
`;

const SectionHeader = styled(Typography)`
  font-size: 28px;
  line-height: 40px;
  font-weight: 700;
`;

const Text = styled(Typography)`
  font-size: 20px;
  line-height: 1.5;
`;

const ListItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
`;

const ListIcon = styled(ListItemIcon)`
  margin-right: 10px;
  min-width: 28px;
`;

const Link = styled.a`
  font-size: 20px;
  line-height: 1.5;
  text-decoration: none;
  color: #2d33f5;
`;

const Divider = styled.hr`
  height: 1px;
  max-width: 90%;
  width: 1280px;
  background-color: #e3e3ff;
  border-width: 0;
`;

const FullWidthImage = styled.img`
  max-width: 100%;
`;

const Card = styled(Box)`
  margin: 20px;
  max-width: 360px;
  padding: 36px;
  background-color: #ffffff;
  border: 1px solid #e3e3ff;
  border-radius: 12px;
`;

const StyledAvatar = styled(Avatar)`
  width: 96px;
  height: auto;
`;

const CardHeader = styled(Typography)`
  font-size: 20px;
`;

const ReadyToQuoteSection = styled(Box)`
  height: 440px;
  background: linear-gradient(to right, #000, #133a57);
  position: relative;
  overflow: hidden;
`;

const WhiteText = styled(MuiTypography)`
  color: #ffffff;
`;

const ReadyToQuoteButton = styled(ButtonLink)`
  background-color: #f6c106;
  color: #21245c;

  &:hover {
    background-color: #f6c106;
  }
`;

const BackgroundImage = styled.img`
  width: 100%;
`;

const Paragraph = ({ children, ...props }) => (
  <Box mb={3} {...props}>
    <Text>{children}</Text>
  </Box>
);

export default function WhatIsCyberInsurance() {
  const Cards = [
    {
      icon: DangerIcon,
      alt: "Danger Icon",
      title: "Attacks are more frequent",
      content:
        "Cyber attacks are becoming more frequent and severe. Cyber criminals are taking advantage of businesses adapting to remote work and relying more on technology.",
    },
    {
      icon: CostIcon,
      alt: "Cost Icon",
      title: "Recovery costs millions",
      content:
        "Cyber incident recovery costs frequently exceed $1M and can easily bankrupt a small business. A recent study shows that 60% of small businesses go bankrupt within six months following a data breach.",
    },
    {
      icon: ContractIcon,
      alt: "Contract Icon",
      title: "It’s a contract requirement",
      content:
        "Cyber insurance is increasingly required when signing contracts for everything from service contracts to equity investments. Purchasing a policy in advance can help secure contracts faster.",
    },
  ];

  const Coverages = [
    {
      id: "Breach Event",
      title: "Breach Response",
      description:
        "Coverage for notification costs, forensic costs, legal expenses, and crisis management in the event of a data security or privacy breach.",
      example:
        "Your database containing customer payment information gets breached. The insurer will pay for security forensics on the breach, and for notifying affected customers about the event.",
    },
    {
      id: "Bricking",
      title: "Bricking",
      description:
        "Coverage in the event that a cyber incident leads to hardware becoming unusable and beyond repair.",
      example:
        "A server at a data center gets hacked. Despite the fact that the malicious software was removed - the server may still be corrupted/untrustworthy (and is therefore unusable). Bricking coverage will cover the cost of replacing the server with a new one.",
    },
    {
      id: "Business Interruption",
      title: "Business Interruption",
      description:
        "Coverage for lost revenues or expenses incurred due to an interruption or outage of an insured’s systems caused by a cyber security breach.",
      example:
        "A cyberattack knocks important servers offline, preventing you from completing sales. The insurer will cover both the revenues lost due to downtime, and the costs to put the servers back online.",
    },
    {
      id: "Contingent Interruption",
      title: "Contingent Business Interruption",
      description:
        "Coverage for lost revenues or costs due to Business Interruption as a consequence of third parties suffering outages that prevent the insured from generating revenue or operating normally.",
      example:
        "The vendor managing your IT system has downtime due to a cyber attack. Consequently, you cannot process new sales. Your insurer will reimburse you for those lost revenues.",
    },
    {
      id: "Contingent Bodily Injury",
      title: "Contingent Bodily Injury",
      description:
        "Coverage for costs due to sickness or bodily harm to a person caused by a cyber event suffered by a third party.",
      example:
        "Your factory has a conveyor belt that is controlled by software managed by a third party. The third party is breached, their software stops working, and conveyor belt malfunctions, injuring a worker. The insurer will pay for the worker’s medical expenses.",
    },
    {
      id: "Cyber Extortion and Ransomware",
      title: "Cyber Extortion",
      description:
        "Also known as ransomware, this is coverage for costs associated with a cyber extortion event, including extortion monies paid as a result of an extortion threat.",
      example:
        "A hacker breaches your customer database and threatens to delete the data unless you pay them a fee. The insurer will cover this fee.",
    },
    {
      id: "Invoice Manipulation",
      title: "Invoice Manipulation",
      description:
        "Coverage for losses due to customer invoices being fraudulently sent or modified.",
      example:
        "A criminal gains access to an employee’s email account, and sends a legitimate-seeming invoice to one of your customers. The customer pays the invoice to the criminal, while your company does not get paid. The insurer will cover the net financial loss to you.",
    },
    {
      id: "Network Security/Privacy Liability",
      title: "Network Security and Privacy Liability",
      description:
        "Coverage for expenses incurred in an alleged privacy or data breach, such as remediation and defense/damages in the event of litigation due to the breach.",
      example:
        "Your customer database is breached and your customers’ passwords are leaked. A customer consequently suffers damage and sues you. The insurer would cover the costs of your litigation defense.",
    },
    {
      id: "Media",
      title: "Media Liability",
      description:
        "Coverage for damages due to the publication of media material (text, sounds, images, etc.) that results in allegations of defamation, slander, trademark or copyright infringement, etc.",
      example:
        "Your company changes its logo. Another company alleges that your new logo infringes on their trademarked logo. The insurer would cover any legal costs in defending your new logo.",
    },
    {
      id: "PCI/DSS",
      title: "Payment Card Industry (“PCI”) Liability",
      description:
        "Also known as Data Security Standard (“DSS”), this is coverage for losses (defense costs and corresponding fines and penalties) due to any alleged or actual noncompliance with PCI security standards.",
      example:
        "Your company accidentally stores some customer credit card numbers in an insecure way. Those numbers become leaked to the public, and some customers suffer damages. The insurer would cover those damages, as well as any additional fines that are assessed.",
    },
    {
      id: "Regulatory",
      title: "Regulatory Liability",
      description:
        "Coverage for legal defense and civil fines or monetary penalties that an insured may be required to pay if investigated by a regulatory authority following a cyber event.",
      example:
        "Your customers’ sensitive data is leaked to the public. A government agency investigates you to assess if you have complied with all applicable privacy/data storage requirements. They find that you have not, and you are fined. The insurer would cover any legal defense costs, as well as the fine.",
    },
    {
      id: "Reputational Harm",
      title: "Reputational Harm",
      description:
        "Coverage for lost revenues or expenses incurred due to an adverse media event following a cyber attack.",
      example:
        "Your corporate LinkedIn is taken over by a hacker and defaced with offensive messages. The insurer would pay for a public relations firm to remedy the situation, and for any lost revenues if sales decrease because of the defacement.",
    },
    {
      id: "Social Engineering",
      title: "Social Engineering",
      description:
        "Coverage for losses due to criminals deceiving the insureds’ employees into taking damaging actions, such as initiating payments to fraudulent actors.",
      example:
        "An employee wires a vendor after receiving an urgent email from the CEO about payment being overdue. It turns out that the email was not from the CEO but from an impersonator. The insurer will cover the loss.",
    },
    {
      id: "System Failure",
      title: "System Failure",
      description:
        "Coverage for an unplanned interruption, suspension, degradation, or failure in the service of computer systems which is not the result of a breach. System failures that could qualify include failures due to human error, power outages, or programming errors (restrictions apply).",
      example:
        "One of your IT associates runs a faulty update to your IT ecosystem knocking all your computers offline for four days. The insurer would cover the revenues lost while your business was offline.",
    },
    {
      id: "Data Recovery",
      title: "Data Recovery",
      description:
        "Data recovery losses are the expenses associated with replacing or recovering damaged, lost, or stolen data and software.",
      example:
        "A cyber breach corrupts your customer database, making thousands of customer records unusable. The insurer would cover the expense of hiring a third party to restore your database to its previous state.",
    },
  ];

  const isLarge = useIsLarge();
  const isMedium = useIsMedium();
  const isMobile = useIsMobile();

  const router = useRouter();
  const expandedCoverage = router.query?.coverage;

  const fieldRefs = Coverages.reduce((obj, coverage) => {
    obj[coverage.id] = createRef();

    return obj;
  }, {});

  useEffect(() => {
    if (expandedCoverage) {
      fieldRefs[expandedCoverage as string]?.current?.scrollIntoView({
        block: "center",
      });
    }
  }, [fieldRefs]);

  return (
    <Box position="relative" width="100%" overflow="hidden">
      <Box
        position="absolute"
        zIndex={-1}
        left={isLarge ? 0 : "calc((100% - 1420px) / 4)"}
      >
        <BackgroundImage
          src={BackgroundWavesLeft}
          alt="What is cyber insurance page background waves"
        />
      </Box>
      <Box
        position="absolute"
        zIndex={-1}
        right={isLarge ? 0 : "calc((100% - 1420px) / 10)"}
      >
        <BackgroundImage
          src={BackgroundWavesRight1}
          alt="What is cyber insurance page background waves"
        />
      </Box>
      <Box
        position="absolute"
        zIndex={-1}
        right={isLarge ? 0 : "calc((100% - 1420px) / 8)"}
      >
        <BackgroundImage
          src={BackgroundWavesRight2}
          alt="What is cyber insurance page background waves"
        />
      </Box>
      <Box
        position="absolute"
        zIndex={-1}
        top={1841}
        right={isLarge ? 0 : "calc((100% - 1420px) / 6)"}
      >
        <BackgroundImage
          src={BackgroundWavesRight3}
          alt="What is cyber insurance page background waves"
        />
      </Box>
      <MainLayout>
        <>
          <StyledContainer>
            <MainHeader variant="h1">
              What is <br /> Cyber Insurance?
            </MainHeader>
            <Box mt={6}>
              <Paragraph>
                Modern businesses depend on digital technology. Technology makes
                them more efficient, but it also leaves them vulnerable to
                technical failures and cyber attacks. Innocent mistakes, like an
                employee clicking on a malicious link in an email, can quickly
                turn into six or seven-figure catastrophes.
              </Paragraph>
              <Paragraph>
                Cyber insurance helps companies mitigate the financial risks and
                liabilities that come with modern technology.
              </Paragraph>
              <Paragraph>
                A single cyber policy can cover the costs of a wide range of
                events, including:
              </Paragraph>
              <Box mb={3}>
                <List>
                  <ListItem>
                    <ListIcon>
                      <img src={Password} alt="Password Icon" />
                    </ListIcon>
                    <Text>Data Breaches</Text>
                  </ListItem>
                  <ListItem>
                    <ListIcon>
                      <img src={PaperFail} alt="Paper Fail Icon" />
                    </ListIcon>
                    <Text>Invoice Scams</Text>
                  </ListItem>
                  <ListItem>
                    <ListIcon>
                      <img src={ShieldFail} alt="Shield Fail Icon" />
                    </ListIcon>
                    <Text>IT Downtime</Text>
                  </ListItem>
                  <ListItem>
                    <ListIcon>
                      <img src={Calendar} alt="Calendar Icon" />
                    </ListIcon>
                    <Text>Equipment Destruction</Text>
                  </ListItem>
                  <ListItem>
                    <ListIcon>
                      <img src={DownArrow} alt="Down Arrow Icon" />
                    </ListIcon>
                    <Link href="#coverage-section">and more </Link>
                  </ListItem>
                </List>
              </Box>
              <Paragraph>
                Many cyber insurance policies include additional risk-preventing
                services including access to expert security services that help
                remedy cyber events as they occur.
              </Paragraph>
            </Box>
          </StyledContainer>

          {!isMobile && <Divider />}

          <Box display="flex" alignItems="center" flexDirection="column">
            <Box
              maxWidth={1280}
              width="100%"
              mt={isMobile ? 6 : 10}
              display="flex"
              flexDirection={isMedium ? "row" : "column-reverse"}
              justifyContent="space-between"
              alignItems="center"
              px={4}
            >
              <Box
                maxWidth={isMedium ? 480 : "100%"}
                display="flex"
                justifyContent="center"
                flexDirection="column"
                alignItems={!isMedium && !isMobile ? "center" : "start"}
                mt={isMedium ? 0 : 5}
              >
                <Box mb={3}>
                  <SectionHeader variant="h2">
                    Every business faces cyber risk
                  </SectionHeader>
                </Box>
                <Box mb={6}>
                  <Typography>
                    Any business that uses technology is at risk. Even a small
                    digital presence carries liability: your corporate Facebook
                    page could be defaced, damaging your reputation and
                    alienating customers, or your email account could be
                    breached, leaking your customers’ data for which you may be
                    liable.
                  </Typography>
                </Box>
                <Box>
                  <ButtonLink
                    color="primary"
                    variant="contained"
                    size="large"
                    route={Links.QUOTE}
                  >
                    Get a Quote
                  </ButtonLink>
                </Box>
              </Box>
              <Box ml={isMedium ? 3 : 0}>
                <FullWidthImage
                  src="/images/people_with_phones.png"
                  alt="People with phones"
                />
              </Box>
            </Box>
            <Box
              maxWidth={1280}
              width="100%"
              mt={10}
              display="flex"
              flexDirection={isMedium ? "row" : "column"}
              justifyContent="space-between"
              alignItems="center"
              px={4}
            >
              <Box mr={isMedium ? 4 : 0}>
                <FullWidthImage
                  src="/images/woman_with_hands_in_air.png"
                  alt="Women with hands in air"
                />
              </Box>
              <Box
                maxWidth={isMedium ? 480 : "100%"}
                display="flex"
                justifyContent="center"
                flexDirection="column"
                alignItems={!isMedium && !isMobile ? "center" : "start"}
                mt={isMedium ? 0 : 5}
              >
                <Box mb={3}>
                  <SectionHeader variant="h2">
                    Cyber risk: more than just hacking
                  </SectionHeader>
                </Box>
                <Box mb={6}>
                  <Typography>
                    Many cyber insurance policies cover more than just attacks
                    on your company. If a vendor you depend on goes offline, you
                    may be covered for lost revenue while you are unable to
                    operate as well as any expenses you incur restarting your
                    operations.
                  </Typography>
                </Box>
                <Box>
                  <ButtonLink
                    color="primary"
                    variant="contained"
                    size="large"
                    route={Links.QUOTE}
                  >
                    Get a Quote
                  </ButtonLink>
                </Box>
              </Box>
            </Box>
            <Box
              maxWidth={1280}
              width="100%"
              mt={10}
              display="flex"
              flexDirection={isMedium ? "row" : "column-reverse"}
              justifyContent="space-between"
              alignItems="center"
              px={4}
            >
              <Box
                maxWidth={isMedium ? 480 : "100%"}
                display="flex"
                justifyContent="center"
                flexDirection="column"
                alignItems={!isMedium && !isMobile ? "center" : "start"}
                mt={isMedium ? 0 : 5}
              >
                <Box mb={3}>
                  <SectionHeader variant="h2">
                    Protect your small business
                  </SectionHeader>
                </Box>
                <Box mb={6}>
                  <Typography>
                    While cyber attacks on large companies are frequently in the
                    news, most cyber attacks are on small companies. It is
                    especially small businesses that are at risk, because they
                    usually have fewer resources to invest in security and to
                    defend themselves. That is precisely why insurance is most
                    useful for small businesses.
                  </Typography>
                </Box>
                <Box>
                  <ButtonLink
                    color="primary"
                    variant="contained"
                    size="large"
                    route={Links.QUOTE}
                  >
                    Get a Quote
                  </ButtonLink>
                </Box>
              </Box>
              <Box ml={isMedium ? 3 : 0}>
                <FullWidthImage
                  src="/images/shelf_with_jars.png"
                  alt="Shelf with jars"
                />
              </Box>
            </Box>
          </Box>

          <Box
            mt={12}
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <Box mb={6} px={4}>
              <Typography variant="h1" style={{ lineHeight: "64px" }}>
                Protect yourself from cyber attacks
              </Typography>
            </Box>
            <Box
              display="flex"
              flexWrap="wrap"
              justifyContent="center"
              maxWidth={1280}
              width="100%"
              mb={6}
            >
              {Cards.map(({ icon, alt, title, content }) => (
                <Card>
                  <Box mb={4}>
                    <StyledAvatar src={icon} alt={alt} />
                  </Box>
                  <Box mb={2}>
                    <CardHeader variant="subtitle1">{title}</CardHeader>
                  </Box>
                  <Typography>{content}</Typography>
                </Card>
              ))}
            </Box>
            <Box mb={12}>
              <ButtonLink
                color="primary"
                variant="contained"
                size="large"
                route={Links.QUOTE}
              >
                Get a Quote
              </ButtonLink>
            </Box>
          </Box>

          <Box
            bgcolor="#F5F5FF"
            display="flex"
            justifyContent="center"
            id="coverage-section"
          >
            <Box maxWidth={1280} width="100%" p={4}>
              <Box mt={8} mb={2}>
                <Typography variant="h1" style={{ fontSize: 32 }}>
                  Common Coverages
                </Typography>
              </Box>
              <Box maxWidth={800} width="100%" mb={6}>
                <Typography>
                  Below are the most common cyber insurance coverages. At
                  GetCyber.com, we want you to have all your bases covered – we
                  will provide you with every coverage below whenever possible,
                  and highlight any limitations or exclusions when you compare
                  quotes.
                </Typography>
              </Box>
              <Box
                mb={6}
                display="flex"
                flexDirection={isMobile ? "column" : "row"}
              >
                <Box maxWidth={isMobile ? "100%" : "50%"} mr={isMobile ? 0 : 5}>
                  {Coverages.slice(0, Math.ceil(Coverages.length / 2)).map(
                    (coverage) => (
                      <CoverageAccordion
                        ref={fieldRefs[coverage.id]}
                        coverage={coverage}
                        expanded={coverage.id === expandedCoverage}
                      />
                    )
                  )}
                </Box>
                <Box maxWidth={isMobile ? "100%" : "50%"}>
                  {Coverages.slice(Math.ceil(Coverages.length / 2)).map(
                    (coverage) => (
                      <CoverageAccordion
                        ref={fieldRefs[coverage.id]}
                        coverage={coverage}
                        expanded={coverage.id === expandedCoverage}
                      />
                    )
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

          <ReadyToQuoteSection>
            <Box
              position="absolute"
              left={isLarge ? 0 : "calc((100% - 1420px) / 2.7)"}
            >
              <BackgroundImage
                src={ReadyToQuoteBgWavesLeft}
                alt="Ready to quote background waves"
              />
            </Box>
            <Box
              position="absolute"
              top={0}
              right={isLarge ? 0 : "calc((100% - 1420px) / 4)"}
            >
              <BackgroundImage
                src={ReadyToQuoteBgWavesRight}
                alt="Ready to quote background waves"
              />
            </Box>
            <Box
              position="absolute"
              top={0}
              width="100%"
              height="100%"
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              p={4}
            >
              <WhiteText variant="h2" style={{ fontWeight: 700 }}>
                Ready to quote?
              </WhiteText>
              <Box mt={1} mb={4}>
                <WhiteText align="center">
                  Stay safe whether your business uses or relies on tech
                </WhiteText>
              </Box>
              <Box>
                <ReadyToQuoteButton
                  variant="contained"
                  size="large"
                  route={Links.QUOTE}
                >
                  Get a Quote
                </ReadyToQuoteButton>
              </Box>
            </Box>
          </ReadyToQuoteSection>
        </>
      </MainLayout>
    </Box>
  );
}
